import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Collapse, Row, Col } from 'antd';
import { CollapseExpandIcon } from '../../../custom_shared/components';
import { BalanceInformationNodeModel } from '../../models';
import { EntryCodeNodeRenderer } from '..';
import {
    BusinessIdInput,
    InsuredPeriodStartInput,
    InsuredPeriodEndInput,
    UnderwritingYearInput,
    CurrencyInput,
    SystemSignedShareInput,
    DocumentSignedShareInput,
    DueDateInput,
    AccountingPeriodInput,
    AccountingYearInput
} from '../../node_inputs/BalanceInformationNodeInputs';
import { InputRenderer, BalanceInformationHeader, AddEntryCodeButton } from '../../components';
import { StyleUtils } from '../../misc';
import './BalanceInformationNodeRenderer.less';

interface Props {
    node: BalanceInformationNodeModel;
    addNodeRef: (nodeId: string, nodeRef: React.RefObject<HTMLDivElement>) => void;
}

const BalanceInformationNodeRenderer: React.FC<Props> = ({ node, addNodeRef }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (containerRef.current) {
            addNodeRef(node.id, containerRef);
        }
    }, [addNodeRef, node.id]);

    return (
        <div ref={containerRef} className="balance-information-node-renderer-container">
            <Collapse
                activeKey={node.expanded ? [node.id] : []}
                onChange={node.toggleExpanded}
                bordered={false}
                expandIcon={CollapseExpandIcon}
            >
                <Collapse.Panel
                    className="balance-information-collapse-panel"
                    header={<span className="header-title">{node.name}</span>}
                    key={node.id}
                >
                    <BalanceInformationHeader node={node} />

                    <Row gutter={10}>
                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={node.businessIdInput} component={BusinessIdInput} />
                        </Col>

                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={node.insuredPeriodStartInput} component={InsuredPeriodStartInput} />
                        </Col>

                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={node.insuredPeriodEndInput} component={InsuredPeriodEndInput} />
                        </Col>
                    </Row>

                    <Row gutter={10}>
                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={node.underwritingYearInput} component={UnderwritingYearInput} />
                        </Col>

                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={node.systemSignedShareInput} component={SystemSignedShareInput} />
                        </Col>

                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={node.documentSignedShareInput} component={DocumentSignedShareInput} />
                        </Col>
                    </Row>

                    <Row gutter={10}>
                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={node.dueDateInput} component={DueDateInput} />
                        </Col>

                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={node.accountingPeriodInput} component={AccountingPeriodInput} />
                        </Col>

                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={node.accountingYearInput} component={AccountingYearInput} />
                        </Col>
                    </Row>

                    <Row gutter={10}>
                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                            <InputRenderer input={node.currencyInput} component={CurrencyInput} />
                        </Col>
                    </Row>

                    {node.entryCodeNodes.length > 0 && (
                        <div className="accounting-details-container">
                            <span className="accounting-details-label">Accounting Details</span>
                            {node.entryCodeNodes.map(entryCodeNode => (
                                <EntryCodeNodeRenderer
                                    key={entryCodeNode.id}
                                    balanceInformationNode={node}
                                    entryCodeNode={entryCodeNode}
                                />
                            ))}
                        </div>
                    )}

                    <AddEntryCodeButton node={node} />
                </Collapse.Panel>
            </Collapse>
        </div>
    );
};

export default observer(BalanceInformationNodeRenderer);
